import { Dispatch, SetStateAction } from "react";

import {
  CommentRepository,
  CommunityRepository,
  ContentFeedType,
  FileRepository,
  getCommentTopic,
  getPostTopic,
  isReportedByMe,
  PostRepository,
  ReactionRepository,
  UserRepository,
} from "@amityco/ts-sdk";
import {
  CommentItem,
  MapAmityCommentOptions,
  MapAmityCommentsOptions,
  MapAmityPostOptions,
  PostItem,
  Reactions,
} from "./types";
import { CommunityGroup } from "@/api";

export const isCommunityAdmin = (user?: Amity.User) => {
  return user?.roles.includes("global-admin") ?? false;
};

export const getAvatar = async (avatarFileId?: string): Promise<string> => {
  if (!avatarFileId) return "";

  try {
    const imageContent = await FileRepository.getFile(avatarFileId);
    return FileRepository.fileUrlWithSize(imageContent.data.fileUrl, "small");
  } catch (error) {
    console.error("Error fetching avatar:", error);
    return "";
  }
};

const getPost = async (id: string): Promise<Amity.Post> => {
  return new Promise<Amity.Post>((resolve, reject) => {
    const unsub = PostRepository.getPost(id, ({ data, loading, error }) => {
      if (!loading) {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      }
    });
    unsub();
  });
};

export const getComment = async (id: string) => {
  return new Promise<Amity.InternalComment>((resolve, reject) => {
    const unsub = CommentRepository.getComment(
      id,
      ({ data, loading, error }) => {
        if (!loading) {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        }
      },
    );
    unsub();
  });
};

const getFileId = (item: {
  fileId?: string;
  videoFileId?: { original?: string };
}): string | undefined => {
  if (item?.fileId && typeof item.fileId === "string") {
    return item.fileId;
  }
  if (
    item?.videoFileId?.original &&
    typeof item.videoFileId.original === "string"
  ) {
    return item.videoFileId.original;
  }
  return undefined;
};

export const getPostAttachments = async (ids: string[]) => {
  try {
    const childrenPosts = await Promise.all(ids.map(getPost));
    const fileIds = childrenPosts.map((p: Amity.Post) => p.data).map(getFileId);
    const fileIdsUndefinedRemoved = fileIds.filter(
      (f: string | undefined): f is string => f !== undefined,
    );
    const files: Amity.Cached<Amity.File<"image" | "video">>[] =
      await Promise.all(
        fileIdsUndefinedRemoved.map((id: string) => FileRepository.getFile(id)),
      );
    return files.map((f) => f.data);
  } catch (error) {
    console.error("Error fetching post attachments:", error);
    return [];
  }
};

export const getCommentAttachments = async (
  attachments: Amity.Attachment[] | undefined,
) => {
  if (!attachments || attachments.length === 0) {
    return [];
  }

  try {
    const fileIds = attachments.map((a) => a.fileId);
    const fileIdsUndefinedRemoved = fileIds.filter(
      (f: string | undefined): f is string => f !== undefined,
    );
    const files = await Promise.all(
      fileIdsUndefinedRemoved.map((id: string) => FileRepository.getFile(id)),
    );
    const imageFiles = files.filter(
      (f) => f.data.type === "image",
    ) as Amity.Cached<Amity.File<"image">>[];
    return imageFiles.map((f) => f.data) as Amity.File<"image">[];
  } catch (error) {
    console.error("Error fetching post attachments:", error);
    return [];
  }
};

export const getUser = async (id: string): Promise<Amity.User> => {
  return new Promise<Amity.User>((resolve, reject) => {
    const unsub = UserRepository.getUser(id, ({ data, loading, error }) => {
      if (!loading) {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      }
    });
    unsub();
  });
};

export const getCommunities = async (
  membership?: "all" | "member" | "notMember",
): Promise<Amity.Community[]> => {
  return new Promise<Amity.Community[]>((resolve, reject) => {
    const params: Amity.CommunityLiveCollection = {
      includeDeleted: false,
      limit: 50,
    };
    if (membership) {
      params.membership = membership;
    }

    const unsub = CommunityRepository.getCommunities(
      params,
      ({ data, loading, error }) => {
        if (!loading) {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        }
      },
    );
    unsub();
  });
};

export const getCommunityById = async (
  id: string,
): Promise<Amity.Community> => {
  return new Promise<Amity.Community>((resolve, reject) => {
    const unsub = CommunityRepository.getCommunity(
      id,
      ({ data, loading, error }) => {
        if (!loading) {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        }
      },
    );
    unsub();
  });
};

const getLiveFieldValues = async (amityPost: Amity.Post) => {
  try {
    const haveLiked =
      amityPost.myReactions?.some((r: Reactions) => r === Reactions.like) ||
      false;
    const comments = amityPost.commentsCount;
    const likes = amityPost.reactions[Reactions.like] ?? 0;
    const textContent = amityPost.data.text;
    const attachments = await getPostAttachments(amityPost.children);
    const flagCount = amityPost.flagCount;

    return {
      textContent,
      attachments,
      likes,
      comments,
      haveLiked,
      flagCount,
    };
  } catch (error) {
    console.error("Error mapping Amity post:", error);
    throw error;
  }
};

export const mapAmityPost = async (
  options: MapAmityPostOptions,
): Promise<PostItem> => {
  try {
    const { post: amityPost, currentUserId } = options;
    const id = amityPost.postId;
    const date = new Date(amityPost.createdAt);
    const postCreator = await getUser(amityPost.postedUserId);
    const isOwner = amityPost.postedUserId === currentUserId;
    const parentFeedId = amityPost.targetId;
    const subscribeTopic = getPostTopic(amityPost);

    const liveFieldValues = await getLiveFieldValues(amityPost);

    return {
      id,
      postCreator,
      date,
      isOwner,
      subscribeTopic,
      parentFeedId,
      ...liveFieldValues,
    };
  } catch (error) {
    console.error("Error mapping Amity post:", error);
    throw error;
  }
};

export const updateMappedPost = async (
  currentMappedPost: PostItem,
  updatedAmityPost: Amity.Post,
) => {
  try {
    const liveFieldValues = await getLiveFieldValues(updatedAmityPost);

    const updatedPost: PostItem = {
      ...currentMappedPost,
      ...liveFieldValues,
    };

    return updatedPost;
  } catch (error) {
    console.error("Error mapping Amity post:", error);
    throw error;
  }
};

export const mapAmityComment = async (
  options: MapAmityCommentOptions,
): Promise<CommentItem> => {
  try {
    const { comment: amityComment, currentUserId } = options;

    const id = amityComment.commentId;
    const haveLiked =
      amityComment.myReactions?.some((r) => r === Reactions.like) || false;
    const repliesCount = amityComment.childrenNumber;
    const likes = amityComment.reactions[Reactions.like] ?? 0;
    const date = new Date(amityComment.createdAt);
    const textContent = amityComment.data
      ? (amityComment.data as Amity.ContentDataText).text
      : "";
    const attachments = await getCommentAttachments(amityComment.attachments);
    const commentCreator = await getUser(amityComment.userId);
    const isOwner = amityComment.userId === currentUserId;
    const subscribeTopic = getCommentTopic(amityComment);
    const children = amityComment.children;
    const referenceId = amityComment.referenceId;
    const flagCount = amityComment.flagCount;
    return {
      id,
      commentCreator,
      date,
      textContent,
      attachments,
      likes,
      repliesCount,
      haveLiked,
      isOwner,
      subscribeTopic,
      children,
      referenceId,
      flagCount,
    };
  } catch (error) {
    console.error("Error mapping Amity comment:", error);
    throw error;
  }
};

export const mapAmityComments = async ({
  comments,
  currentUserId,
}: MapAmityCommentsOptions): Promise<CommentItem[]> => {
  try {
    return await Promise.all(
      comments.map((comment) => mapAmityComment({ comment, currentUserId })),
    );
  } catch (error) {
    console.error("Error mapping Amity comments:", error);
    return [];
  }
};

export const uploadMedia = async (
  file: File,
  setUploadProgress: (progress: number) => void,
) => {
  const type = file.type.includes("video") ? "video" : "image";
  const data = new FormData();
  data.append("files", file);

  if (type === "video") {
    const { data: video } = await FileRepository.uploadVideo(
      data,
      ContentFeedType.POST,
      (percent: number) => {
        setUploadProgress(percent);
      },
    );
    return video[0];
  }

  const { data: image } = await FileRepository.uploadImage(
    data,
    (percent: number) => {
      setUploadProgress(percent);
    },
  );

  return image[0];
};

export const toggleReaction = async (
  action: "add" | "remove",
  type: Amity.ReactableType,
  id: string,
  reaction: string,
) => {
  try {
    if (action === "remove") {
      return await ReactionRepository.removeReaction(type, id, reaction);
    }
    return await ReactionRepository.addReaction(type, id, reaction);
  } catch (error) {
    console.error("Error toggling reaction:", error);
    throw error;
  }
};

export const checkReportStatus = async (
  type: "post" | "comment",
  id: string,
  setIsChecking: Dispatch<SetStateAction<boolean>>,
  setHaveReported: Dispatch<SetStateAction<boolean>>,
) => {
  setIsChecking(true);
  try {
    const reported = await isReportedByMe(type, id);
    setHaveReported(reported);
  } catch (error) {
    console.error("Error checking report status:", error);
  } finally {
    setIsChecking(false);
  }
};

export const isAutoJoinGroup = (group: CommunityGroup) => {
  return ["main", "automatic"].includes(group.Type);
};
